// src/components/admin/UnitDialog.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const UnitDialog = ({ unit, onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    name: '',
    name_en: '',
    name_km: '',
    category: 'piece',
    is_sellable: 1,
    is_purchasable: 1,
    sort_order: 0
  });

  useEffect(() => {
    if (unit) {
      setFormData({
        name: unit.name || '',
        name_en: unit.name_en || '',
        name_km: unit.name_km || '',
        category: unit.category || 'piece',
        is_sellable: unit.is_sellable ?? 1,
        is_purchasable: unit.is_purchasable ?? 1,
        sort_order: unit.sort_order || 0
      });
    }
  }, [unit]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const categories = [
    { value: 'piece', label: { zh: '件数', en: 'Piece', km: 'ចំនួន' } },
    { value: 'weight', label: { zh: '重量', en: 'Weight', km: 'ទម្ងន់' } },
    { value: 'volume', label: { zh: '体积', en: 'Volume', km: 'ទំហំ' } },
    { value: 'length', label: { zh: '长度', en: 'Length', km: 'បណ្តោយ' } },
    { value: 'other', label: { zh: '其他', en: 'Other', km: 'ផ្សេងទៀត' } }
  ];

  const getCategoryLabel = (cat) => {
    return cat.label[language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh'];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {unit ? (
              language === 'en' ? 'Edit Unit' :
              language === 'km' ? 'កែសម្រួលឯកតា' :
              '编辑单位'
            ) : (
              language === 'en' ? 'Add Unit' :
              language === 'km' ? 'បន្ថែមឯកតា' :
              '添加单位'
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 单位名称 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {language === 'zh' ? '单位名称 (中文) *' :
                 language === 'en' ? 'Unit Name (Chinese) *' :
                 'ឈ្មោះឯកតា (ចិន) *'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="件"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                {language === 'zh' ? '单位名称 (英文)' :
                 language === 'en' ? 'Unit Name (English)' :
                 'ឈ្មោះឯកតា (អង់គ្លេស)'}
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => handleChange('name_en', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Piece"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">
                {language === 'zh' ? '单位名称 (高棉语)' :
                 language === 'en' ? 'Unit Name (Khmer)' :
                 'ឈ្មោះឯកតា (ខ្មែរ)'}
              </label>
              <input
                type="text"
                value={formData.name_km}
                onChange={(e) => handleChange('name_km', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="ដុំ"
              />
            </div>
          </div>

          {/* 类别选择 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Category *' :
               language === 'km' ? 'ប្រភេទ *' :
               '类别 *'}
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </div>

          {/* 排序权重 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Sort Order' :
               language === 'km' ? 'លំដាប់តម្រៀប' :
               '排序权重'}
            </label>
            <input
              type="number"
              value={formData.sort_order}
              onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              {language === 'en' ? 'Higher values appear first' :
               language === 'km' ? 'តម្លៃខ្ពស់បង្ហាញមុន' :
               '数值越大越靠前'}
            </p>
          </div>

          {/* 使用场景 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_sellable"
                checked={formData.is_sellable === 1}
                onChange={(e) => handleChange('is_sellable', e.target.checked ? 1 : 0)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="is_sellable" className="ml-2 text-sm">
                {language === 'en' ? 'Available for sales' :
                 language === 'km' ? 'អាចប្រើសម្រាប់លក់' :
                 '可用于销售'}
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_purchasable"
                checked={formData.is_purchasable === 1}
                onChange={(e) => handleChange('is_purchasable', e.target.checked ? 1 : 0)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="is_purchasable" className="ml-2 text-sm">
                {language === 'en' ? 'Available for purchase' :
                 language === 'km' ? 'អាចប្រើសម្រាប់ទិញ' :
                 '可用于采购'}
              </label>
            </div>
          </div>

          {/* 提示信息 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              {language === 'en' 
                ? '💡 Tip: Adding multilingual names helps customers understand the unit better.'
                : language === 'km'
                ? '💡 ចំណាំ: ការបន្ថែមឈ្មោះពហុភាសាជួយអតិថិជនយល់ឯកតាកាន់តែច្បាស់។'
                : '💡 提示: 添加多语言名称帮助客户更好地理解单位。'}
            </p>
          </div>

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {unit ? t.save : (
                language === 'en' ? 'Add' :
                language === 'km' ? 'បន្ថែម' :
                '添加'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitDialog;